import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/Users/PrivateRoute';
import Login from './components/Login/Login';
import Dashboard from './components/Dashboard/Dashboard';
import ActiveUsers from './components/Dashboard/ActiveUsers';
import Users from './components/Users/Users';
import Layout from './components/Layout';
import SessionList from './components/Users/SessionList';
import Conversations from './components/Chat/Conversations';
import DocumentExport from './components/DocumentExport';
import Chat from './components/Chat/Chat';
import TestChat from './components/Chat/TestChat';
import Hal from './components/Chat/Hal';
import Panels from './components/Panels/Panels';
import LLMPrompts from './components/Panels/LLMPrompts';
import Stations from './components/Stations/Stations';
import Biometrics from './components/Biometrics/Biometrics';
import Lakes from './components/Lakes/Lakes';
import LakeDetail from './components/Lakes/LakeDetail';
import NotAuthorized from './components/NotAuthorized';
import './App.css';
import './components/Login/Login.css';
import Turns from './components/Chat/Turns';
import UserDetails from './components/Users/UserDetails';
import BiometricActivity from './components/Stations/BiometricActivity';
import FishLogGallery from './components/Users/FishLogGallery';
import Agents from './components/Agents/Agents';
import WaterTemps from './components/WaterTemps/WaterTemps';
import Announcements from './components/Announcements/Announcements';
import AnnouncementDetail from './components/Announcements/AnnouncementDetail';
import Offers from './components/Offers/Offers';
import OfferDetail from './components/Offers/OfferDetail';
import HelpContext from './components/HelpContext/HelpContext';
import HelpContextDetail from './components/HelpContext/HelpContextDetail';
import Photos from './components/Photos/Photos';
import MoonPosition from './components/MoonPosition/MoonPosition';
import KnowledgeBase from './components/KnowledgeBase/KnowledgeBase';
import VectorDBSearch from './components/AI/VectorDBSearch';

function App() {
  // Role constants to avoid typos
  const ADMIN = 'Admin';
  const LAKE_MANAGER = 'Lake Manager';

  return (
    <Provider store={store}>
      <Router>
        <AuthProvider>
          <div className="App">
          <Routes>
            <Route path="/" element={<Login />} />

            {/* Admin only routes */}
            <Route 
              path="/dashboard" 
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route 
              path="/active-users" 
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <ActiveUsers />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/users"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Users />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/users/:userId"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <UserDetails />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/users/:userId/fish-log"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <FishLogGallery />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/photos"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Photos />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/stations"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Stations />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/biometrics"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Biometrics />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/moon-position"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <MoonPosition />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Lakes routes - Admin and Lake Manager */}
            <Route
              path="/lakes"
              element={
                <PrivateRoute allowedRoles={[ADMIN, LAKE_MANAGER]}>
                  <Layout>
                    <Lakes />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/lakes/:lakeId"
              element={
                <PrivateRoute allowedRoles={[ADMIN, LAKE_MANAGER]}>
                  <Layout>
                    <LakeDetail />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/biometric-activity/:stationId"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <BiometricActivity />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/agents"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Agents />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/panels"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Panels />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/llm-prompts"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <LLMPrompts />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/conversations"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Conversations />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/turns"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Turns />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/hal"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Hal />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/conversations/:sessionId"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Conversations />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/document-export"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <DocumentExport />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Chat />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/test-chat"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <TestChat />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/water-temps"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <WaterTemps />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/announcements"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Announcements />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/offers"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <Offers />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/offers/:offerId"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <OfferDetail />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/announcements/:announcementId"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <AnnouncementDetail />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/help-context"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <HelpContext />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/help-context/:contextId"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <HelpContextDetail />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/knowledge-base"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <KnowledgeBase />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/vector-db-search"
              element={
                <PrivateRoute allowedRoles={[ADMIN]}>
                  <Layout>
                    <VectorDBSearch />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route path="/not-authorized" element={<NotAuthorized />} />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </div>
        </AuthProvider>
      </Router>
    </Provider>
  );
}

export default App;
